import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard, Eye, EyeOff, Loader2, Save, CheckCircle2, XCircle,
  AlertTriangle, TestTube,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Input } from '@ouropix/ui';
import { Label } from '@ouropix/ui';
import { Switch } from '@ouropix/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@ouropix/ui';
import { Separator } from '@ouropix/ui';
import { Skeleton } from '@ouropix/ui';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { apiErrorMessage } from '@/lib/api-helpers';
import AdminPageHeader from '@/components/layout/PageHeader';
import { useAuth } from '@/hooks/useAuth';

export const Route = createFileRoute('/_admin/integracoes')({
  component: IntegracoesPage,
});

// ── Types ─────────────────────────────────────────────────────

interface ProviderConfig {
  success: boolean;
  provider: string;
  environment: string;
  api_key_preview: string;
  webhook_url: string;
  is_active: boolean;
  updated_at: string;
  updated_by: string;
  has_config: boolean;
}

// ── Hooks ──────────────────────────────────────────────────────

function useProviderConfig(provider: string, environment: string) {
  return useQuery<ProviderConfig>({
    queryKey: ['provider-config', provider, environment],
    queryFn: () =>
      api.get(`/admin/integrations/payment/${provider}`, { params: { environment } }).then((r) => r.data),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
}

function useUpdateProviderConfig(provider: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      environment: string;
      api_key: string;
      webhook_secret: string;
      webhook_url: string;
      is_active: boolean;
    }) => api.patch(`/admin/integrations/payment/${provider}`, body).then((r) => r.data),
    onSuccess: (_data, vars) => {
      toast.success('Credenciais salvas com sucesso');
      queryClient.invalidateQueries({ queryKey: ['provider-config', provider, vars.environment] });
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao salvar credenciais')),
  });
}

function useTestConnection(provider: string) {
  return useMutation({
    mutationFn: (environment: string) =>
      api.post(`/admin/integrations/payment/${provider}/test`, null, { params: { environment } }).then((r) => r.data),
    onSuccess: (data: { success: boolean; message: string }) => {
      if (data.success) {
        toast.success(data.message || 'Conexão OK');
      } else {
        toast.error(data.message || 'Falha na conexão');
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Erro ao testar conexão')),
  });
}

// ── ProviderEnvForm ────────────────────────────────────────────

interface ProviderEnvFormProps {
  provider: string;
  environment: 'sandbox' | 'prod';
  systemMode: string;
  canEdit: boolean;
}

function ProviderEnvForm({ provider, environment, systemMode, canEdit }: ProviderEnvFormProps) {
  const { data, isLoading } = useProviderConfig(provider, environment);
  const update = useUpdateProviderConfig(provider);
  const test = useTestConnection(provider);

  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);

  // Sync from loaded data (only once)
  const [synced, setSynced] = useState(false);
  if (data && !synced) {
    setWebhookUrl(data.webhook_url ?? '');
    setIsActive(data.is_active);
    setSynced(true);
  }

  const isProdActive = environment === 'prod' && systemMode !== 'prod';

  function handleSave() {
    if (!apiKey || !webhookSecret) {
      toast.error('API Key e Webhook Secret são obrigatórios');
      return;
    }
    update.mutate({ environment, api_key: apiKey, webhook_secret: webhookSecret, webhook_url: webhookUrl, is_active: isActive });
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-4 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {isProdActive && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Produção só fica ativa quando o sistema estiver em modo <strong className="ml-1">prod</strong>.
        </div>
      )}

      {data?.has_config && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Credencial atual:</span>
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
            {data.api_key_preview || '(sem preview)'}
          </code>
          <Badge variant={data.is_active ? 'default' : 'secondary'} className="text-xs">
            {data.is_active ? 'Ativo' : 'Inativo'}
          </Badge>
        </div>
      )}

      <div className="grid gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`api-key-${environment}`}>API Key (client_id:client_secret)</Label>
          <div className="relative">
            <Input
              id={`api-key-${environment}`}
              type={showApiKey ? 'text' : 'password'}
              placeholder={data?.has_config ? '••••••••• (deixe em branco para manter)' : 'clientId:clientSecret'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="pr-10"
              disabled={!canEdit}
            />
            <button
              type="button"
              onClick={() => setShowApiKey((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">Formato: <code>clientId:clientSecret</code></p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`webhook-secret-${environment}`}>Webhook Secret (HMAC-SHA256)</Label>
          <div className="relative">
            <Input
              id={`webhook-secret-${environment}`}
              type={showWebhookSecret ? 'text' : 'password'}
              placeholder={data?.has_config ? '••••••••• (deixe em branco para manter)' : 'webhook_secret'}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              className="pr-10"
              disabled={!canEdit}
            />
            <button
              type="button"
              onClick={() => setShowWebhookSecret((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`webhook-url-${environment}`}>Webhook URL (opcional)</Label>
          <Input
            id={`webhook-url-${environment}`}
            type="url"
            placeholder="https://api.ouropix.com.br/api/payments/webhook/deposit"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            disabled={!canEdit}
          />
        </div>

        <div className="flex items-center gap-3">
          <Switch
            id={`active-${environment}`}
            checked={isActive}
            onCheckedChange={setIsActive}
            disabled={!canEdit || (isProdActive && environment === 'prod')}
          />
          <Label htmlFor={`active-${environment}`} className="cursor-pointer">
            Ativo para pagamentos
          </Label>
        </div>
      </div>

      <Separator />

      <div className="flex items-center gap-2">
        {canEdit && (
          <Button
            onClick={handleSave}
            disabled={update.isPending}
            size="sm"
          >
            {update.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Salvar
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => test.mutate(environment)}
          disabled={test.isPending || !data?.has_config}
        >
          {test.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <TestTube className="mr-2 h-4 w-4" />
          )}
          Testar conexão
        </Button>

        {test.data && (
          <span className="flex items-center gap-1 text-sm">
            {(test.data as { success: boolean }).success ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-red-500" />
            )}
            {(test.data as { message: string }).message}
          </span>
        )}
      </div>
    </div>
  );
}

// ── EfiCard ────────────────────────────────────────────────────

function EfiCard({ systemMode }: { systemMode: string }) {
  const { session } = useAuth();
  const isSuperadmin = session?.role === 'superadmin';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <CreditCard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base">EFI</CardTitle>
              <CardDescription className="text-xs">Gateway PIX (Cash-In e Cash-Out)</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            Sistema: <span className="ml-1 font-semibold">{systemMode === 'prod' ? 'Produção' : 'Dev/Sandbox'}</span>
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="sandbox">
          <TabsList className="mb-4">
            <TabsTrigger value="sandbox">Sandbox</TabsTrigger>
            <TabsTrigger value="prod">Produção</TabsTrigger>
          </TabsList>
          <TabsContent value="sandbox">
            <ProviderEnvForm
              provider="efi"
              environment="sandbox"
              systemMode={systemMode}
              canEdit={isSuperadmin}
            />
          </TabsContent>
          <TabsContent value="prod">
            <ProviderEnvForm
              provider="efi"
              environment="prod"
              systemMode={systemMode}
              canEdit={isSuperadmin}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────

function IntegracoesPage() {
  const { data: systemModeData } = useQuery<{ mode: string }>({
    queryKey: ['system-mode-admin'],
    queryFn: () => api.get('/admin/system/mode').then((r) => r.data),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });

  const systemMode = systemModeData?.mode ?? 'dev';

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Integrações"
        description="Configuração de gateways de pagamento e APIs externas."
      />
      <EfiCard systemMode={systemMode} />
    </div>
  );
}
